// Turnover Control Tower — demand-side surface (Sprint 6).
//
// Gives apartment managers a portfolio-level view of their turnover batches:
// how many are covered, to confirm, unscheduled or at risk, without opening
// every booking. It is strictly PLATFORM-ONLY:
//
//   * it queries `booking_batches` / `bookings` owned by the caller only;
//   * it consumes the demand RPCs (`get_demand_portfolio_summary`,
//     `get_demand_batch_status`) which never read the private supply table;
//   * it never imports the supply-side Tracker types or any supply hook.
//
// The confidentiality boundary is enforced by
// `src/test/schedulerConfidentiality.test.ts` (demand prefix list).

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronDown, ClipboardList, Clock, HelpCircle, LifeBuoy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import {
  CONFIDENCE_BADGE_CLASS,
  RISK_LABEL_BADGE_CLASS,
  aggregateRiskCounts,
  computeRiskLabel,
  formatRiskReason,
  getConfidenceCopy,
  getConfidenceLevel,
  trackSchedulerActionClicked,
  trackSchedulerActionShown,
  trackSchedulerSummaryViewed,
  type ClientFallbackInput,
  type RiskLabel,
} from "@/lib/scheduler";
import {
  useDemandBatchStatus,
  useDemandPortfolioSummary,
} from "@/hooks/scheduler";
import {
  COVERAGE_LABEL,
  computeCoverageCounters,
  coverageOf,
  isCancelled,
  rowFlagsOf,
  type Coverage,
} from "./coverage";

// Forward-looking window for the portfolio summary — same horizon the supply
// Tracker uses in Sprint 5, so the two sides stay comparable on platform data.
const PORTFOLIO_WINDOW_DAYS = 60;

interface BatchRow {
  id: string;
  created_at: string;
  rows_count: number | null;
  status: string | null;
  company_name: string | null;
  service_name: string | null;
}

interface DemandBookingRow {
  id: string;
  booking_number: string | null;
  status: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  window_start_at: string | null;
  window_end_at: string | null;
  checkout_at: string | null;
  checkin_at: string | null;
  service_address_text: string | null;
  company_name: string | null;
  service_name: string | null;
  batch_id: string | null;
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function TurnoverControlTower() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [bookingsByBatch, setBookingsByBatch] = useState<Record<string, DemandBookingRow[]>>({});

  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const [vendorFilter, setVendorFilter] = useState<string>("all");
  const [coverageFilter, setCoverageFilter] = useState<string>("all");
  const [onlyAtRisk, setOnlyAtRisk] = useState<boolean>(false);
  const [areaSearch, setAreaSearch] = useState<string>("");

  // ------------------------------------------------------------------
  // Data load: own profile + own batches + their platform bookings.
  // ------------------------------------------------------------------
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          if (active) setLoading(false);
          return;
        }
        const prof = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", session.user.id)
          .maybeSingle();
        const pid = prof.data?.id ?? null;
        if (!active) return;
        setProfileId(pid);

        const { data: batchRows } = await supabaseUntyped
          .from("booking_batches")
          .select("id, created_at, rows_count, status, companies(company_name), services(name)")
          .eq("created_by_user_id", session.user.id)
          .order("created_at", { ascending: false })
          .limit(30);

        const batchList: BatchRow[] = ((batchRows as Array<Record<string, unknown>>) || []).map((b) => ({
          id: String(b.id),
          created_at: String(b.created_at ?? ""),
          rows_count: typeof b.rows_count === "number" ? b.rows_count : null,
          status: (b.status as string) ?? null,
          company_name: (b.companies as { company_name?: string } | null)?.company_name ?? null,
          service_name: (b.services as { name?: string } | null)?.name ?? null,
        }));
        const ids = batchList.map((b) => b.id).filter(Boolean);

        const byBatch: Record<string, DemandBookingRow[]> = {};
        if (ids.length > 0) {
          const { data: bkRows } = await supabaseUntyped
            .from("bookings")
            .select("id, booking_number, status, scheduled_date, scheduled_time, window_start_at, window_end_at, checkout_at, checkin_at, service_address_text, companies(company_name), services(name), batch_id")
            .in("batch_id", ids)
            .order("created_at", { ascending: false });
          for (const r of (bkRows as Array<Record<string, unknown>>) || []) {
            const bid = String(r.batch_id ?? "");
            if (!bid) continue;
            (byBatch[bid] ||= []).push({
              id: String(r.id),
              booking_number: (r.booking_number as string) ?? null,
              status: (r.status as string) ?? null,
              scheduled_date: (r.scheduled_date as string) ?? null,
              scheduled_time: (r.scheduled_time as string) ?? null,
              window_start_at: (r.window_start_at as string) ?? null,
              window_end_at: (r.window_end_at as string) ?? null,
              checkout_at: (r.checkout_at as string) ?? null,
              checkin_at: (r.checkin_at as string) ?? null,
              service_address_text: (r.service_address_text as string) ?? null,
              company_name: (r.companies as { company_name?: string } | null)?.company_name ?? null,
              service_name: (r.services as { name?: string } | null)?.name ?? null,
              batch_id: bid,
            });
          }
        }

        if (!active) return;
        setBatches(batchList);
        setBookingsByBatch(byBatch);
      } catch {
        // non-blocking: the rest of the dashboard still works.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const now = Date.now();
  const windowDates = useMemo(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + PORTFOLIO_WINDOW_DAYS);
    return { startDate: toISODate(start), endDate: toISODate(end) };
  }, []);

  const allBookings = useMemo(
    () => Object.values(bookingsByBatch).flat().filter((b) => !isCancelled(b.status)),
    [bookingsByBatch],
  );

  // ------------------------------------------------------------------
  // Portfolio summary (server engine + client fallback).
  // ------------------------------------------------------------------
  const portfolioFallback = useCallback((): ClientFallbackInput => {
    const rowFlags = allBookings.map((b) => rowFlagsOf(b, now));
    return { rowFlags };
  }, [allBookings, now]);

  const portfolio = useDemandPortfolioSummary({
    profileId,
    startDate: windowDates.startDate,
    endDate: windowDates.endDate,
    clientFallback: portfolioFallback,
    enabled: !!profileId,
  });

  useEffect(() => {
    if (!portfolio.data || !portfolio.source || !profileId) return;
    trackSchedulerSummaryViewed({
      surface: "demand_control_tower",
      scope: "demand_portfolio",
      source: portfolio.source,
      profileId,
      startDate: windowDates.startDate,
      endDate: windowDates.endDate,
      summary: portfolio.data,
    });
  }, [portfolio.data, portfolio.source, profileId, windowDates.startDate, windowDates.endDate]);

  // ------------------------------------------------------------------
  // Coverage counters (client-side, from own platform bookings).
  // ------------------------------------------------------------------
  const counters = useMemo(() => computeCoverageCounters(allBookings, now), [allBookings, now]);

  const vendorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const b of batches) if (b.company_name) set.add(b.company_name);
    return Array.from(set);
  }, [batches]);

  // ------------------------------------------------------------------
  // Per-batch derived view (client risk label + coverage breakdown).
  // ------------------------------------------------------------------
  const batchCards = useMemo(() => {
    return batches.map((batch) => {
      const list = (bookingsByBatch[batch.id] || []).filter((b) => !isCancelled(b.status));
      const flags = list.map((b) => rowFlagsOf(b, now));
      const counts = aggregateRiskCounts(flags);
      const riskLabel = computeRiskLabel(counts);
      const coverage = { coperti: 0, da_confermare: 0, non_pianificati: 0 };
      for (const b of list) coverage[coverageOf(b)] += 1;
      const atRisk = flags.filter((f) => f.urgent || f.tight_window).length;
      const areaText = list
        .map((b) => b.service_address_text || "")
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return { batch, list, counts, riskLabel, coverage, atRisk, areaText };
    });
  }, [batches, bookingsByBatch, now]);

  const filteredCards = useMemo(() => {
    const q = areaSearch.trim().toLowerCase();
    return batchCards.filter((c) => {
      if (vendorFilter !== "all" && c.batch.company_name !== vendorFilter) return false;
      if (onlyAtRisk && c.atRisk === 0) return false;
      if (coverageFilter !== "all") {
        const key = coverageFilter as Coverage;
        if ((c.coverage[key] ?? 0) === 0) return false;
      }
      if (q && !c.areaText.includes(q)) return false;
      return true;
    });
  }, [batchCards, vendorFilter, onlyAtRisk, coverageFilter, areaSearch]);

  const sourceLabel =
    portfolio.source === "server"
      ? "motore server v1"
      : portfolio.source === "client_fallback"
      ? "fallback locale"
      : "calcolo locale";

  return (
    <div className="space-y-6">
      {/* Portfolio summary banner */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent-green-500/5 shadow-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 break-words">
                <ClipboardList className="h-5 w-5 text-primary" />
                Turnover Control Tower
              </CardTitle>
              <CardDescription className="break-words">
                Stato del tuo portfolio turnover nei prossimi {PORTFOLIO_WINDOW_DAYS} giorni: copertura, conferme e
                rischio operativo, senza aprire ogni prenotazione.
              </CardDescription>
            </div>
            {portfolio.data && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={RISK_LABEL_BADGE_CLASS[portfolio.data.risk_label]}>
                  {portfolio.data.risk_label}
                </Badge>
                <Badge
                  variant="outline"
                  className={CONFIDENCE_BADGE_CLASS[getConfidenceLevel(portfolio.data.confidence_score)]}
                  title={getConfidenceCopy(getConfidenceLevel(portfolio.data.confidence_score))}
                >
                  affidabilita {getConfidenceLevel(portfolio.data.confidence_score)}
                </Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {portfolio.data?.suggested_action && (
            <p className="text-sm text-gray-700">{portfolio.data.suggested_action}</p>
          )}
          {portfolio.data && portfolio.data.risk_reasons.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {portfolio.data.risk_reasons.map((r) => (
                <Badge key={`${r.code}-${r.scope}`} variant="secondary" className="bg-white/70 text-gray-700">
                  {formatRiskReason(r)}
                </Badge>
              ))}
            </div>
          )}

          {/* Coverage counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CounterTile icon={<CheckCircle2 className="h-4 w-4" />} label={COVERAGE_LABEL.coperti} value={counters.coperti} tone="emerald" />
            <CounterTile icon={<Clock className="h-4 w-4" />} label={COVERAGE_LABEL.da_confermare} value={counters.da_confermare} tone="amber" />
            <CounterTile icon={<CalendarClock className="h-4 w-4" />} label={COVERAGE_LABEL.non_pianificati} value={counters.non_pianificati} tone="slate" />
            <CounterTile icon={<AlertTriangle className="h-4 w-4" />} label={COVERAGE_LABEL.a_rischio} value={counters.a_rischio} tone="red" />
          </div>

          <div className="text-[11px] text-gray-400">Calcolo: {sourceLabel} · solo prenotazioni di piattaforma</div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-gray-200 shadow-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-full sm:w-52"><SelectValue placeholder="Fornitore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i fornitori</SelectItem>
                {vendorOptions.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={coverageFilter} onValueChange={setCoverageFilter}>
              <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Copertura" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le coperture</SelectItem>
                <SelectItem value="coperti">{COVERAGE_LABEL.coperti}</SelectItem>
                <SelectItem value="da_confermare">{COVERAGE_LABEL.da_confermare}</SelectItem>
                <SelectItem value="non_pianificati">{COVERAGE_LABEL.non_pianificati}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Filtra per area / indirizzo"
              value={areaSearch}
              onChange={(e) => setAreaSearch(e.target.value)}
              className="w-full sm:w-56"
            />
            <Button
              variant={onlyAtRisk ? "default" : "outline"}
              onClick={() => setOnlyAtRisk((v) => !v)}
              className="whitespace-nowrap"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Solo a rischio
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch list */}
      {loading ? (
        <div className="text-sm text-gray-500 text-center py-10">Caricamento portfolio…</div>
      ) : filteredCards.length === 0 ? (
        <Card className="border-gray-200 shadow-card">
          <CardContent className="py-10 text-center space-y-3">
            <ClipboardList className="h-10 w-10 mx-auto text-gray-300" />
            <p className="text-gray-600">
              {batches.length === 0
                ? "Non hai ancora caricato turnover. Usa il Bulk Order per importarli."
                : "Nessun batch corrisponde ai filtri."}
            </p>
            {batches.length === 0 && (
              <Button onClick={() => navigate("/bulk-order")}>Vai al Bulk Order</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCards.map((card) => (
            <BatchCard
              key={card.batch.id}
              card={card}
              isOpen={openBatchId === card.batch.id}
              onToggle={() => setOpenBatchId((cur) => (cur === card.batch.id ? null : card.batch.id))}
              navigateSupport={() => navigate("/support")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Counter tile
// ---------------------------------------------------------------------------

const TONE_CLASS: Record<string, string> = {
  emerald: "border-emerald-200 text-emerald-700",
  amber: "border-amber-200 text-amber-700",
  slate: "border-slate-200 text-slate-700",
  red: "border-red-200 text-red-700",
};

function CounterTile({ icon, label, value, tone }: { icon: ReactNode; label: string; value: number; tone: string }) {
  return (
    <div className={`rounded-xl border bg-white/80 px-3 py-3 ${TONE_CLASS[tone] ?? "border-gray-200 text-gray-700"}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Batch card (with lazy server-backed detail)
// ---------------------------------------------------------------------------

interface BatchCardData {
  batch: BatchRow;
  list: DemandBookingRow[];
  counts: ReturnType<typeof aggregateRiskCounts>;
  riskLabel: RiskLabel;
  coverage: { coperti: number; da_confermare: number; non_pianificati: number };
  atRisk: number;
}

function BatchCard({
  card,
  isOpen,
  onToggle,
  navigateSupport,
}: {
  card: BatchCardData;
  isOpen: boolean;
  onToggle: () => void;
  navigateSupport: () => void;
}) {
  const now = Date.now();
  const { batch, list, riskLabel, coverage } = card;

  // Client fallback for the detail summary, computed from this batch's rows.
  const batchFallback = useCallback((): ClientFallbackInput => {
    const rowFlags = list.map((b) => rowFlagsOf(b, now));
    return { rowFlags };
  }, [list, now]);

  const detail = useDemandBatchStatus({
    batchId: batch.id,
    clientFallback: batchFallback,
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen || !detail.data || !detail.source) return;
    trackSchedulerSummaryViewed({
      surface: "demand_control_tower",
      scope: "demand_batch",
      source: detail.source,
      batchId: batch.id,
      summary: detail.data,
    });
    trackSchedulerActionShown({
      surface: "demand_control_tower",
      scope: "demand_batch",
      actionId: "batch_suggested_action",
      actionLabel: detail.data.suggested_action,
      source: detail.source,
      batchId: batch.id,
      riskLabel: detail.data.risk_label,
      reasonCodes: detail.data.risk_reasons.map((r) => r.code),
    });
  }, [isOpen, detail.data, detail.source, batch.id]);

  const effectiveRisk = detail.data?.risk_label ?? riskLabel;

  const fireCta = (actionId: string, actionLabel: string) => {
    trackSchedulerActionClicked({
      surface: "demand_control_tower",
      scope: "demand_batch",
      actionId,
      actionLabel,
      source: detail.source ?? "client_fallback",
      batchId: batch.id,
      riskLabel: effectiveRisk,
      reasonCodes: detail.data?.risk_reasons.map((r) => r.code) ?? [],
    });
    navigateSupport();
  };

  return (
    <Card className="border-gray-200 shadow-card overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full text-left">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 break-words">
                  {batch.company_name || "Fornitore"}{batch.service_name ? ` · ${batch.service_name}` : ""}
                </span>
                <Badge variant="outline" className={RISK_LABEL_BADGE_CLASS[effectiveRisk]}>{effectiveRisk}</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : ""} · {batch.rows_count ?? list.length} turnover
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-xs">
                <span className="text-emerald-700">{coverage.coperti} coperti</span>
                <span className="text-amber-700">{coverage.da_confermare} da confermare</span>
                <span className="text-slate-600">{coverage.non_pianificati} non pianificati</span>
              </div>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </div>
          </div>
        </CardContent>
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-4 bg-gray-50/50">
          {detail.data && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={RISK_LABEL_BADGE_CLASS[detail.data.risk_label]}>{detail.data.risk_label}</Badge>
                <Badge
                  variant="outline"
                  className={CONFIDENCE_BADGE_CLASS[getConfidenceLevel(detail.data.confidence_score)]}
                  title={getConfidenceCopy(getConfidenceLevel(detail.data.confidence_score))}
                >
                  affidabilita {getConfidenceLevel(detail.data.confidence_score)}
                </Badge>
              </div>
              <p className="text-sm text-gray-700">{detail.data.suggested_action}</p>
              {detail.data.risk_reasons.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {detail.data.risk_reasons.map((r) => (
                    <Badge key={`${r.code}-${r.scope}`} variant="secondary" className="bg-white text-gray-700">
                      {formatRiskReason(r)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Rows */}
          <div className="space-y-2">
            {list.map((b) => {
              const f = rowFlagsOf(b, now);
              return (
                <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-800 truncate">
                      {b.booking_number || b.id.slice(0, 8)}{b.service_address_text ? ` · ${b.service_address_text}` : ""}
                    </div>
                    <div className="text-xs text-gray-500">
                      {b.scheduled_date ? `${b.scheduled_date}${b.scheduled_time ? ` ${b.scheduled_time}` : ""}` : "senza data"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    {f.urgent && <Badge variant="outline" className="border-red-300 text-red-700">in scadenza</Badge>}
                    {f.tight_window && <Badge variant="outline" className="border-amber-300 text-amber-700">finestra stretta</Badge>}
                    {f.unscheduled && <Badge variant="outline" className="border-slate-300 text-slate-700">da pianificare</Badge>}
                    {f.same_day_turnover && <Badge variant="outline" className="border-violet-300 text-violet-700">same-day</Badge>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Assisted CTAs (Sprint 6: tracked + light flow to support) */}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button size="sm" onClick={() => fireCta("request_confirmation", "Richiedi conferma")}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Richiedi conferma
            </Button>
            <Button size="sm" variant="outline" onClick={() => fireCta("request_coverage_support", "Richiedi supporto copertura")}>
              <LifeBuoy className="h-4 w-4 mr-2" />
              Richiedi supporto copertura
            </Button>
            <span className="flex items-center gap-1 text-[11px] text-gray-400 sm:ml-auto">
              <HelpCircle className="h-3 w-3" />
              {detail.source === "server" ? "motore server v1" : detail.source === "client_fallback" ? "fallback locale" : "calcolo locale"}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
